import { html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhPlus";
import "@phosphor-icons/webcomponents/PhUsers";
import "ui-components/button/wow-button";
import "ui-components/confirmation/wow-confirmation";
import "ui-components/empty-state/empty-state";

import { styles } from "./communities-page.css";
import { SignalElement, inject } from "ui-common/di";
import type { IAuthState } from "ui-common/services/auth/types";
import type { ICreatorCommunitiesState, ICreatorCommunitiesService } from "../../services/creator-communities/types";
import type { IBackofficeFeatureFlagsState } from "../../services/backoffice-feature-flags/types";
import { NotificationService } from "ui-components/notification";
import type { Community } from "api-contracts/rpc/community";

import "../../components/page-content/page-content";
import "../../components/stat-badge/stat-badge";
import "../../components/view-primary-buttons/view-primary-buttons";
import "../../components/share-content-modal/share-content-modal";
import "../../components/communities-table/communities-table";
import type { ShareContentDetails } from "../../components/share-content-modal/share-content-modal";
import { t, translate } from "../../translations/i18n";
import { navigateTo } from "../../router";

type CommunityFilter = "all" | "published" | "draft" | "archived";

@customElement("communities-page")
export class CommunitiesPage extends SignalElement {
  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  @inject<ICreatorCommunitiesState>("creatorCommunitiesState")
  private communitiesState!: ICreatorCommunitiesState;

  @inject<ICreatorCommunitiesService>("creatorCommunitiesService")
  private communitiesService!: ICreatorCommunitiesService;

  @inject<IBackofficeFeatureFlagsState>("backofficeFeatureFlagsState")
  private featureFlagsState!: IBackofficeFeatureFlagsState;

  static override styles = [styles];

  @state()
  private selectedFilter: CommunityFilter = "all";

  @state()
  private deletingCommunityId?: string;

  @state()
  private archivingCommunityId?: string;

  @state()
  private showShareModal = false;

  @state()
  private shareContentDetails?: ShareContentDetails;

  override connectedCallback() {
    super.connectedCallback();
    this.communitiesService.loadCommunities();
  }

  private handleFilterChange(e: Event) {
    this.selectedFilter = (e.target as HTMLSelectElement).value as CommunityFilter;
  }

  private handleCreateClick() {
    navigateTo("communities/create");
  }

  private handleEditClick(communityId: string) {
    navigateTo("communities/edit", true, [communityId]);
  }

  private handleDeleteClick(communityId: string) {
    this.deletingCommunityId = communityId;
  }

  private handleCancelDelete() {
    this.deletingCommunityId = undefined;
  }

  private async handleConfirmDelete() {
    if (!this.deletingCommunityId) return;

    try {
      await this.communitiesService.deleteCommunity(this.deletingCommunityId);
      NotificationService.success(translate("communitiesPage.deleteSuccess"));
      this.deletingCommunityId = undefined;
    } catch (error) {
      console.error("Error deleting community:", error);
      NotificationService.error(translate("communitiesPage.deleteError"));
    }
  }

  private handleArchiveClick(communityId: string) {
    this.archivingCommunityId = communityId;
  }

  private handleCancelArchive() {
    this.archivingCommunityId = undefined;
  }

  private async handleConfirmArchive() {
    if (!this.archivingCommunityId) return;

    try {
      await this.communitiesService.archiveCommunity(this.archivingCommunityId);
      NotificationService.success(translate("communitiesPage.archiveSuccess"));
      this.archivingCommunityId = undefined;
    } catch (error) {
      console.error("Error archiving community:", error);
      NotificationService.error(translate("communitiesPage.archiveError"));
    }
  }

  private async handleDuplicateClick(communityId: string) {
    try {
      await this.communitiesService.duplicateCommunity(communityId);
      NotificationService.success(translate("communitiesPage.duplicateSuccess"));
    } catch (error) {
      console.error("Error duplicating community:", error);
      NotificationService.error(translate("communitiesPage.duplicateError"));
    }
  }

  private handleShareClick(community: Community) {
    const isProduction = window.location.hostname.endsWith("worldofwow.com");
    const baseUrl = isProduction ? "https://worldofwow.com" : "https://worldofwow.dev";
    const handle = this.authState.currentUser.get()?.handle ?? "";
    this.shareContentDetails = {
      id: community.id,
      title: community.title,
      thumbnail_url: community.thumbnail_url,
      shareUrl: `${baseUrl}/${handle}/communities/${community.id}`,
    };
    this.showShareModal = true;
  }

  private handleShareModalClose() {
    this.showShareModal = false;
    this.shareContentDetails = undefined;
  }

  private handleViewClick() {
    const handle = this.authState.currentUser.get()?.handle;
    if (handle) window.open(`/${handle}/communities`, "_self", "noopener,noreferrer");
  }

  private handleEventEditCommunity(e: CustomEvent<{ community: Community }>) {
    this.handleEditClick(e.detail.community.id);
  }

  private handleEventDeleteCommunity(e: CustomEvent<{ community: Community }>) {
    this.handleDeleteClick(e.detail.community.id);
  }

  private handleEventArchiveCommunity(e: CustomEvent<{ community: Community }>) {
    this.handleArchiveClick(e.detail.community.id);
  }

  private handleEventDuplicateCommunity(e: CustomEvent<{ community: Community }>) {
    this.handleDuplicateClick(e.detail.community.id);
  }

  private handleEventShareCommunity(e: CustomEvent<{ community: Community }>) {
    this.handleShareClick(e.detail.community);
  }

  private getFilteredCommunities(communities: Community[]): Community[] {
    switch (this.selectedFilter) {
      case "published":
        return communities.filter((c) => c.status === "published");
      case "draft":
        return communities.filter((c) => c.status === "draft");
      case "archived":
        return communities.filter((c) => c.status === "archived");
      case "all":
      default:
        return communities.filter((c) => c.status !== "archived");
    }
  }

  private getTotalMemberCount(communities: Community[]): number {
    return communities.reduce((sum, c) => sum + c.member_count, 0);
  }

  private renderMainContent() {
    const flagsLoading = this.featureFlagsState.loading.get();
    const hasCommunities = this.featureFlagsState.hasFeature("communities");

    if (!hasCommunities) {
      if (flagsLoading) {
        return html`<div class="main-content"><div class="loading-state"></div></div>`;
      }
      return html`<div class="main-content">${this.renderEmptyState()}</div>`;
    }

    const isLoading = this.communitiesState.isLoading.get();

    if (isLoading) {
      return html`
        <div class="main-content">
          <div class="loading-state">
            <div>${t("communitiesPage.loadingCommunities")}</div>
          </div>
        </div>
      `;
    }

    const communities = this.communitiesState.communities.get();

    if (communities.length === 0) {
      return html`<div class="main-content">${this.renderEmptyState()}</div>`;
    }

    const counts = this.communitiesState.counts.get();
    const filteredCommunities = this.getFilteredCommunities(communities);
    const totalMembers = this.getTotalMemberCount(communities);

    return html`
      <div class="main-content">
        <div>
          <div class="communities-header">
            <h3 class="section-title">
              ${t("communitiesPage.myCommunities")}
              <span class="member-badge">
                <ph-users size="14"></ph-users>
                ${totalMembers}
                ${totalMembers === 1
                  ? translate("communitiesPage.memberSingular")
                  : translate("communitiesPage.memberPlural")}
              </span>
            </h3>
            <div class="communities-filter">
              <wow-button
                size="small"
                variant=${this.selectedFilter === "all" ? "primary" : "subtle"}
                @click=${() => (this.selectedFilter = "all")}
              >
                ${t("communitiesPage.filterAll")} (${counts.total - counts.archived})
              </wow-button>
              <wow-button
                size="small"
                variant=${this.selectedFilter === "published" ? "primary" : "subtle"}
                @click=${() => (this.selectedFilter = "published")}
                ?disabled=${counts.published === 0}
              >
                ${t("communitiesPage.filterPublished")} (${counts.published})
              </wow-button>
              ${counts.draft > 0
                ? html`
                    <wow-button
                      size="small"
                      variant=${this.selectedFilter === "draft" ? "primary" : "subtle"}
                      @click=${() => (this.selectedFilter = "draft")}
                    >
                      ${t("communitiesPage.filterDraft")} (${counts.draft})
                    </wow-button>
                  `
                : nothing}
              ${counts.archived > 0
                ? html`
                    <wow-button
                      size="small"
                      variant=${this.selectedFilter === "archived" ? "primary" : "subtle"}
                      @click=${() => (this.selectedFilter = "archived")}
                    >
                      ${t("communitiesPage.filterArchived")} (${counts.archived})
                    </wow-button>
                  `
                : nothing}
            </div>
            <select class="communities-filter-mobile" .value=${this.selectedFilter} @change=${this.handleFilterChange}>
              <option value="all">${t("communitiesPage.filterAll")} (${counts.total - counts.archived})</option>
              <option value="published" ?disabled=${counts.published === 0}>
                ${t("communitiesPage.filterPublished")} (${counts.published})
              </option>
              ${counts.draft > 0
                ? html`<option value="draft">${t("communitiesPage.filterDraft")} (${counts.draft})</option>`
                : nothing}
              ${counts.archived > 0
                ? html`<option value="archived">${t("communitiesPage.filterArchived")} (${counts.archived})</option>`
                : nothing}
            </select>
          </div>
          ${filteredCommunities.length > 0
            ? html`
                <communities-table
                  .communities=${filteredCommunities}
                  @edit-community=${this.handleEventEditCommunity}
                  @delete-community=${this.handleEventDeleteCommunity}
                  @archive-community=${this.handleEventArchiveCommunity}
                  @duplicate-community=${this.handleEventDuplicateCommunity}
                  @share-community=${this.handleEventShareCommunity}
                ></communities-table>
              `
            : nothing}
        </div>
      </div>
    `;
  }

  private renderEmptyState() {
    return html`
      <div>
        <h3 class="section-title">${t("communitiesPage.myCommunities")}</h3>
        <wow-empty-state
          headline=${translate("communitiesPage.emptyStateHeadline")}
          subtitle=${translate("communitiesPage.emptyStateSubtitle")}
          button-text=${translate("communitiesPage.emptyStateButton")}
          @cta-click=${this.handleCreateClick}
        >
          <ph-plus slot="icon" size="24"></ph-plus>
        </wow-empty-state>
      </div>
    `;
  }

  override render() {
    return html`
      <page-content .pageTitle=${translate("communitiesPage.pageTitle")}>
        <stat-badge slot="center" .count=${this.getTotalMemberCount(this.communitiesState.communities.get())}>
          <ph-users size="18" slot="icon"></ph-users>
          ${t("communitiesPage.totalMembers")}
        </stat-badge>
        <div slot="actions">
          <view-primary-buttons
            primaryText=${translate("communitiesPage.createCommunityButton")}
            ?primaryDisabled=${!this.featureFlagsState.hasFeature("communities")}
            @primary-click=${this.handleCreateClick}
            @view-click=${this.handleViewClick}
          ></view-primary-buttons>
        </div>

        <div class="containers">
          <div class="communities-content">${this.renderMainContent()}</div>
        </div>
      </page-content>

      ${this.deletingCommunityId
        ? html`
            <wow-confirmation header=${translate("communitiesPage.deleteConfirmHeader")}>
              <div class="delete-confirmation-content">
                <p>${t("communitiesPage.deleteConfirmMessage")}</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="medium" @click=${this.handleConfirmDelete}>
                    ${t("communitiesPage.confirmDelete")}
                  </wow-button>
                  <wow-button variant="neutral" size="medium" @click=${this.handleCancelDelete}>
                    ${t("communitiesPage.cancel")}
                  </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
      ${this.archivingCommunityId
        ? html`
            <wow-confirmation header=${translate("communitiesPage.archiveConfirmHeader")}>
              <div class="delete-confirmation-content">
                <p>${t("communitiesPage.archiveConfirmMessage")}</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="medium" @click=${this.handleConfirmArchive}>
                    ${t("communitiesPage.confirmArchive")}
                  </wow-button>
                  <wow-button variant="neutral" size="medium" @click=${this.handleCancelArchive}>
                    ${t("communitiesPage.cancel")}
                  </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
      ${this.showShareModal && this.shareContentDetails
        ? html`
            <share-content-modal
              .content=${this.shareContentDetails}
              @modal-close=${this.handleShareModalClose}
            ></share-content-modal>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "communities-page": CommunitiesPage;
  }
}
